import React from 'react';
import './ErrorBoundary.css';

/**
 * Error Message Component
 * 
 * A reusable component for displaying error messages consistently throughout the application.
 * This component can be used for form validation errors, API errors, or any other error
 * that needs to be displayed to the user.
 * 
 * @param {Object} props - Component props
 * @param {string} props.message - The error message to display
 * @param {string} props.type - The type of error (info, warning, error, critical)
 * @param {boolean} props.dismissible - Whether the error can be dismissed
 * @param {Function} props.onDismiss - Callback function when the error is dismissed
 * @param {string} props.className - Additional CSS class names
 */
const ErrorMessage = ({
  message,
  type = 'error',
  dismissible = false,
  onDismiss,
  className = ''
}) => {
  if (!message) return null;

  // Determine the appropriate CSS class based on error type
  const getTypeClass = () => {
    switch (type) {
      case 'info':
        return 'component-error-info';
      case 'warning':
        return 'component-error-warning';
      case 'critical':
        return 'component-error-critical';
      case 'error':
      default:
        return 'component-error';
    }
  };

  return (
    <div className={`${getTypeClass()} error-message-animation ${className}`}>
      {type === 'error' && (
        <span className="component-error-icon" role="img" aria-label="Error">⚠️</span>
      )}
      {type === 'critical' && (
        <span className="component-error-icon" role="img" aria-label="Critical Error">🛑</span>
      )}
      {type === 'warning' && (
        <span className="component-error-icon" role="img" aria-label="Warning">⚠️</span>
      )}
      {type === 'info' && (
        <span className="component-error-icon" role="img" aria-label="Information">ℹ️</span>
      )}

      <span>{message}</span>

      {dismissible && onDismiss && (
        <button
          className="component-error-dismiss"
          onClick={onDismiss}
          aria-label="Dismiss error"
        >
          ×
        </button>
      )}
    </div>
  );
};

export default ErrorMessage;
